import { useEffect, useState } from "react";
import { ReactComponent as RefreshIcon } from "assets/refresh.svg";
import { useDropzone } from "react-dropzone";
import { Column, FormGroup, ImagePreview, LabelButton, Wrapper } from "./styles";
import { ReactComponent as UploadImageIcon } from "assets/upload-picture.svg";
import { usePolicemanRegister } from "contexts/register/PolicemanContext";
import { IPolicemanPictures } from "interfaces/Policeman";

type DocumentationFormProps = {
  defaultPicturesObject?: IPolicemanPictures;
};

export default function DocumentationForm({
  defaultPicturesObject,
}: DocumentationFormProps) {
  const { setFiles, setPreview, files, preview } = usePolicemanRegister();

  const [acceptedFiles, setAcceptedFiles] = useState(undefined);
  const [lastPhoto, setLastPhoto] = useState(undefined);
  const [selectedField, setSelectedField] = useState<
    | "documentFront"
    | "documentBack"
    | "selfie"
    | "selfieWithDoc"
    | "vehicle"
    | ""
  >("");
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles: any) => {
      setAcceptedFiles(acceptedFiles);
      setLastPhoto(
        acceptedFiles.map((file: Blob | MediaSource) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/jpg": [],
    },
    multiple: false,
    maxFiles: 1,
  });

  // set Files and Preview Image after accepted files changes
  useEffect(() => {
    if (selectedField && acceptedFiles && lastPhoto) {
      setPreview((prev) => ({ ...prev, [selectedField]: lastPhoto }));
      setFiles((prev) => ({ ...prev, [selectedField]: acceptedFiles[0] }));
      return;
    }
  }, [acceptedFiles, lastPhoto, selectedField]);

  return (
    <Wrapper>
      <Column>
        <FormGroup>
          <label htmlFor="document-front">Foto da frente do documento</label>
          <LabelButton
            {...getRootProps()}
            tabIndex={0}
            isFileSelected={!!files.documentFront}
            onFocus={() => setSelectedField("documentFront")}
          >
            <input
              autoComplete="off"
              type="file"
              accept="image/png, image/jpeg"
              id="document-front"
              {...getInputProps()}
            />

            {defaultPicturesObject?.documentFront || files.documentFront ? (
              <div>
                <RefreshIcon />
                <span>Clique para escolher outra foto</span>
                {(defaultPicturesObject?.documentFront ||
                  preview.documentFront) && (
                  <ImagePreview
                    src={
                      preview?.documentFront?.[0].preview ??
                      defaultPicturesObject?.documentFront
                    }
                    alt=""
                  />
                )}
                {files.documentFront && <p>{files.documentFront.name}</p>}
              </div>
            ) : (
              <div>
                <UploadImageIcon />
                <p>Enviar foto</p>
              </div>
            )}
          </LabelButton>
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="document-back">Foto do verso do documento</label>
          <LabelButton
            {...getRootProps()}
            tabIndex={0}
            isFileSelected={!!files.documentBack}
            onFocus={() => setSelectedField("documentBack")}
          >
            <input
              autoComplete="off"
              type="file"
              accept="image/png, image/jpeg"
              id="document-back"
              {...getInputProps()}
            />

            {defaultPicturesObject?.documentBack || files.documentBack ? (
              <div>
                <RefreshIcon />
                <span>Clique para escolher outra foto</span>
                {(defaultPicturesObject?.documentBack ||
                  preview.documentBack) && (
                  <ImagePreview
                    src={
                      preview?.documentBack?.[0].preview ??
                      defaultPicturesObject?.documentBack
                    }
                    alt=""
                  />
                )}
                {files.documentBack && <p>{files.documentBack.name}</p>}
              </div>
            ) : (
              <div>
                <UploadImageIcon />
                <p>Enviar foto</p>
              </div>
            )}
          </LabelButton>
        </FormGroup>
      </Column>

      <Column>
        <FormGroup>
          <label htmlFor="selfie">Selfie do PR</label>
          <LabelButton
            {...getRootProps()}
            tabIndex={0}
            isFileSelected={!!files.selfie}
            onFocus={() => setSelectedField("selfie")}
          >
            <input
              autoComplete="off"
              type="file"
              accept="image/png, image/jpeg"
              id="selfie"
              {...getInputProps()}
            />

            {defaultPicturesObject?.selfie || files.selfie ? (
              <div>
                <RefreshIcon />
                <span>Clique para escolher outra foto</span>
                {(defaultPicturesObject?.selfie || preview.selfie) && (
                  <ImagePreview
                    src={
                      preview?.selfie?.[0].preview ??
                      defaultPicturesObject?.selfie
                    }
                    alt=""
                  />
                )}
                {files.selfie && <p>{files.selfie.name}</p>}
              </div>
            ) : (
              <div>
                <UploadImageIcon />
                <p>Enviar foto</p>
              </div>
            )}
          </LabelButton>
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="selfie-with-document">
            Selfie do PR com documento em mãos
          </label>
          <LabelButton
            {...getRootProps()}
            tabIndex={0}
            isFileSelected={!!files.selfieWithDoc}
            onFocus={() => setSelectedField("selfieWithDoc")}
          >
            <input
              autoComplete="off"
              type="file"
              accept="image/png, image/jpeg"
              id="selfie-with-document"
              {...getInputProps()}
            />

            {defaultPicturesObject?.selfieWithDoc || files.selfieWithDoc ? (
              <div>
                <RefreshIcon />
                <span>Clique para escolher outra foto</span>
                {(defaultPicturesObject?.documentFront ||
                  preview.selfieWithDoc) && (
                  <ImagePreview
                    src={
                      preview.selfieWithDoc?.[0].preview ??
                      defaultPicturesObject?.selfieWithDoc
                    }
                    alt=""
                  />
                )}
                {files.selfieWithDoc && <p>{files.selfieWithDoc.name}</p>}
              </div>
            ) : (
              <div>
                <UploadImageIcon />
                <p>Enviar foto</p>
              </div>
            )}
          </LabelButton>
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="vehicle">Foto do veículo</label>
          <LabelButton
            {...getRootProps()}
            tabIndex={0}
            isFileSelected={!!files.vehicle}
            onFocus={() => setSelectedField("vehicle")}
          >
            <input
              autoComplete="off"
              type="file"
              accept="image/png, image/jpeg"
              id="vehicle"
              {...getInputProps()}
            />

            {defaultPicturesObject?.vehicle || files.vehicle ? (
              <div>
                <RefreshIcon />
                <span>Clique para escolher outra foto</span>
                {(defaultPicturesObject?.vehicle || preview.vehicle) && (
                  <ImagePreview
                    src={
                      preview?.vehicle?.[0].preview ??
                      defaultPicturesObject?.vehicle
                    }
                    alt=""
                  />
                )}
                {files.vehicle && <p>{files.vehicle.name}</p>}
              </div>
            ) : (
              <div>
                <UploadImageIcon />
                <p>Enviar foto</p>
              </div>
            )}
          </LabelButton>
        </FormGroup>
      </Column>
    </Wrapper>
  );
}
